import {Component, Input, Output,
	EventEmitter, ViewChild}                    from '@angular/core';
import {NgbTypeaheadSelectItemEvent}            from "@ng-bootstrap/ng-bootstrap";

import {Player}                                 from "../../services/player/player";
import {PlayerService}                          from "../../services/player/player.service";

import {Observable}                             from "rxjs/Observable";
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/merge';
import {of}                                     from "rxjs/observable/of";
import {isNullOrUndefined}                      from "util";



@Component({
	selector: 'player-typeahead',
	providers: [PlayerService],
	template: `

        <div
		        [ngClass]="setSize('div')"
		    	class="row"
		        style="margin-bottom: 0;"
        >

	        <div [ngClass]="setSize('divcol12')">

                <label class="font-weight-bold" for="player-name-typeahead-http">Player [
                    <span *ngIf="! searching">*</span>
                    <span *ngIf="searching">
                    	<img src="/assets/typeahead.gif" style="width: 16px; margin-top: 2px; cursor: default;" />
                	</span>
                    ]</label>

                <span class="fa fa-question-circle fa-fw" title="Use the underscore as wildcard"></span>
		        
	            <input #typeaheadPlayerNameInput
	                   id="player-name-typeahead-http"
	                   type="text"
	                   class="form-control"
	                   [ngClass]="setSize('input')"
	                   [ngbTypeahead]="playerSearch"
	                   placeholder="Last name,First name"
	                   [resultFormatter]="playerListFormatter"
	                   [inputFormatter]="playerNameFormatter"
	                   (selectItem)="playerSelectedEvent($event)"
	                   (onBlur)="checkField()"
	                   maxlength="64"
					   (keyup.enter)="enterPressedEvent()"
	            />
	
	        </div>
	
        </div>

        <div [ngClass]="setSize('divcol12')" *ngIf="searchFailed">

            <div class="form-control-feedback">Player information unavailable</div>
            
        </div>
	        
	`,
})


export class PlayerTypeaheadComponent {

	// These are semaphores used to hide or display messages.
	hideSearchingWhenUnsubscribed = new Observable(() => () => this.searching = false);
	searchFailed = false;
	searching = false;


	/**
	 * Initialize the search service.
	 *
	 * @param playerService  The service used to find players.
	 */
	constructor(private playerService: PlayerService) {}


	@Input()
	public clearPlayerNameInputField:boolean = false; // Clear the player name INPUT field after a player choice is made, instead of filling it with the choice.


	@Input()
	public code:string;  // The League code ('NFL', 'NBA', etc.)  This comes in from the parent page.


	@Input()
	public size:string;  // The size of the components.  This comes in from the parent page.


	@Input()
	public team:number;  // The Team ID.  This comes in from the parent page.


	@Output()
	public onPlayerChange = new EventEmitter<Player>();  // This notifies the parent page a Player has been selected.

	@Output() 
	public onEnterPressed = new EventEmitter<any>(); // This notifies the parent page of an Enter key press

	/**
	 * Controls the Player Name choices INPUT.
	 */
	@ViewChild('typeaheadPlayerNameInput')
	typeaheadPlayerNameInput: any;


	/**
	 * This is a target for a Name field action.
	 * It is triggered when the user leaves the field.  It checks
	 * to see if the field is blank.  If it is, then the
	 * search is stopped.
	 */
	checkField():void {

		if (0 !== this.typeaheadPlayerNameInput.nativeElement.value.length) {

			return;

		}

		// If we get here, they left one of the fields with nothing in it, so cancel the search.
		this.searchFailed = false;
		this.searching = false;

	}

	/**
	 * Used by the typeahead to format the display of search
	 * results.
	 *
	 * @param player  The Player object selected by the user.
	 */
	playerListFormatter = (player: Player) => player.firstname + ' ' + player.surname
								+ ' (' + player.position_title + '), ' + player.team_title;


	/**
	 * Used by the typeahead to format the display of the name
	 * of the found player.  If the clearPlayerNameInputField is
	 * true, then the string returned is empty. Otherwise, the
	 * Player's data is returned formatted.
	 *
	 * @param player  The Player object to get the name from.
	 */
	playerNameFormatter = (player: Player) => {

		if (this.clearPlayerNameInputField) {

			return '';

		} else {

			return player.firstname + ' ' + player.surname + ' (' + player.position_title + '), ' + player.team_title;
		}

	};


	/**
	 * The typeahead puts the search terms into the
	 * PlayerService and processes the results.  It also
	 * sets the semaphores to show/hide typeahead
	 * progress.
	 *
	 * @return The Player Observable that will return data or errors.
	 */
	playerSearch = (players: Observable<Player[]>) =>
		players
			.debounceTime(300)
			.distinctUntilChanged()
			.do(() => this.searching = true)
			.switchMap(() =>
				this.playerService.createSearch(
					this.typeaheadPlayerNameInput.nativeElement.value,
					this.code,
					this.team
				)
				.do((players: Player[]) => this.searchFailed = 0 === players.length)
				.catch(() => {
					this.searchFailed = true;
					return of([]);
				}))
			.do(() => this.searching = false)
			.merge(this.hideSearchingWhenUnsubscribed);


	/**
	 * The typeahead calls this method when the user makes
	 * a selection.  This notifies the parent page with an event
	 * that passes the selected Player object.
	 *
	 * @param selection  The event that holds the Player choice made by the user.
	 */
	playerSelectedEvent(selection: NgbTypeaheadSelectItemEvent): void {

		this.onPlayerChange.emit(selection.item);

	}


	/**
	 * Read the size variable passed in from the hosting template
	 * and set the controls based on that.
	 *
	 * If size is null, '' is returned.  The other choices are
	 * 'sm', 'lg' and unadorned versions of 'form-control'.
	 *
	 * @param {string}  control  The type of control to return a value for.
	 * @returns {string}  The appropriate Bootstrap value for the form type.
	 */
	setSize(control:string): string {

		let level:number;
		let values:string[];

		if (isNullOrUndefined(this.size) || 0 === this.size.length) {

			level = 1;  // Default size.

		} else {

			// Figure out the size to return.
			let match:string = this.size.toLowerCase().substr(0,1);
			switch (match) {

				case 's' : {
					level = 0;
					break;
				}

				case 'l' : {
					level = 2;
					break;
				}

				default : {
					level = 1;
					break;
				}

			}

		}

		// Return the right string for each type of control.
		switch(control) {

			case 'div': {
				values = ['form-group-sm row', 'form-group row', 'form-group-lg row'];
				return values[level];
			}

			case 'divcol6': {
				values = ['col-sm-6', 'col-6', 'col-lg-6'];
				return values[level];
			}

			case 'divcol12': {
				values = ['col-sm-12', 'col-12', 'col-lg-12'];
				return values[level];
			}

			case 'input': {
				values = ['form-control-sm', '', 'form-control-lg'];
				return values[level];
			}
		}

		return '';

	}

	/**
	 * The typeahead calls this method whenever the Enter key is pressed
	 */
	enterPressedEvent(): void {
		this.onEnterPressed.emit();
	}
}



// WEBPACK FOOTER //
// ./src/app/typeaheads/player/player-typeahead.component.ts